import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { ChannelThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { and, eq, isUndefined, useLiveQuery } from "@tanstack/react-db";
import { memo, useMemo } from "react";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";
import {
  NewSupportTicketModal,
  type ExistingDraftData,
} from "./new-support-ticket-modal";

/**
 * Renders the new support-ticket modal pre-filled with an existing draft.
 */
export const DraftSupportTicketModal = memo(function DraftSupportTicketModal({
  supportTicket,
  thread,
  onClose,
  onCreated,
}: {
  supportTicket: SupportTicketListItemResponse;
  thread: ChannelThread;
  onClose: () => void;
  onCreated: (supportTicketId: string) => void;
}) {
  const collections = useGlobalSupportTicketCollections();
  const draftResult = useLiveQuery(
    (q) =>
      q
        .from({ draft: collections.drafts })
        .where(({ draft }) =>
          and(
            eq(draft.supportTicketId, String(supportTicket.id)),
            eq(draft.threadId, String(thread.id)),
            isUndefined(draft.inReplyToMessageId),
            eq(draft.kind, "merchant"),
          ),
        )
        .findOne(),
    [supportTicket.id, thread.id],
  );
  const threadDraft = draftResult.data ?? null;

  const subject =
    thread.integrationType === "gmail" || thread.integrationType === "outlook"
      ? (thread.channelData?.subject ?? undefined)
      : undefined;

  const existingDraft = useMemo<ExistingDraftData>(
    () => ({
      supportTicketId: supportTicket.id,
      threadId: thread.id,
      integrationId: thread.integrationId,
      customerEmail: supportTicket.customer?.email ?? undefined,
      cc:
        threadDraft?.channelData?.cc
          ?.map((r: { email: string }) => r.email)
          .join(", ") || undefined,
      bcc:
        threadDraft?.channelData?.bcc
          ?.map((r: { email: string }) => r.email)
          .join(", ") || undefined,
      subject: subject ?? undefined,
      draftContent: threadDraft?.content ?? undefined,
    }),
    [
      supportTicket.id,
      supportTicket.customer?.email,
      thread.id,
      thread.integrationId,
      subject,
      threadDraft?.content,
      threadDraft?.channelData,
    ],
  );

  return (
    <NewSupportTicketModal
      existingDraft={existingDraft}
      onCreated={onCreated}
      open
      setOpen={(open) => {
        if (!open) {
          onClose();
        }
      }}
    />
  );
});
